import React from 'react';
import { useForm, useFormState } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { FieldSelect, FieldSingleDateController } from '../../../components';

import css from './DateTimeFields.module.css';

// Selectable times, on the hour. The last start option is 23:00 so a selection always has
// at least one later end option to pair with.
const MINUTES_STEP = 60;

/**
 * Build the list of selectable clock times as { value: 'HH:mm', label: 'HH:mm' }.
 *
 * @param {number} fromMinutes - first selectable time, in minutes from midnight
 * @returns {Array<{value: string, label: string}>}
 */
export const getTimeOptions = (fromMinutes = 0) => {
  const options = [];
  for (let minutes = fromMinutes; minutes < 24 * 60; minutes += MINUTES_STEP) {
    const hh = `${Math.floor(minutes / 60)}`.padStart(2, '0');
    const mm = `${minutes % 60}`.padStart(2, '0');
    options.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
  }
  return options;
};

/** 'HH:mm' as minutes from midnight, or null when unparseable. */
export const parseTimeToMinutes = time => {
  const match = typeof time === 'string' ? time.match(/^(\d{2}):(\d{2})$/) : null;
  if (!match) {
    return null;
  }
  return Number.parseInt(match[1], 10) * 60 + Number.parseInt(match[2], 10);
};

/**
 * Read a "09:00,17:00" query parameter into its two halves.
 *
 * Anything malformed yields empty values rather than a half-applied filter.
 *
 * @param {String} timesValue - the "times" query parameter
 * @returns {{startTime: string|null, endTime: string|null}}
 */
export const parseTimesParam = timesValue => {
  const raw = timesValue ? `${timesValue}`.split(',') : [];
  const startTime = raw[0] || null;
  const endTime = raw[1] || null;
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  const isOrdered = startMinutes != null && endMinutes != null && endMinutes > startMinutes;
  return isOrdered ? { startTime, endTime } : { startTime: null, endTime: null };
};

/**
 * The contents of the dates filter: one day, then the hours on that day.
 *
 * This is the same shape as the search bar's own date filter, so the two agree about what
 * a search means. It is a plain set of Final Form fields, which is what the surrounding
 * FilterPopup / FilterPlain wrappers expect as children.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Function} [props.controllerRef] - Ref callback handed to the calendar
 * @returns {JSX.Element}
 */
const DateTimeFields = props => {
  const intl = useIntl();
  const { className, rootClassName, controllerRef } = props;

  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const { startTime, endTime } = values || {};

  const startMinutes = parseTimeToMinutes(startTime);
  // Drop the very last start option, so an end time after it always exists.
  const startOptions = getTimeOptions(0).slice(0, -1);
  const endOptions = getTimeOptions(
    startMinutes != null ? startMinutes + MINUTES_STEP : MINUTES_STEP
  );

  // Clear an end time that the new start time has invalidated.
  const handleStartTimeChange = value => {
    const newStart = parseTimeToMinutes(value);
    const currentEnd = parseTimeToMinutes(endTime);
    if (newStart != null && currentEnd != null && currentEnd <= newStart) {
      form.change('endTime', undefined);
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <FieldSingleDateController
        className={css.datePicker}
        name="date"
        id="date"
        controllerRef={controllerRef}
      />

      <div className={css.times}>
        <FieldSelect
          className={classNames(css.timeField, { [css.timeFieldActive]: !!startTime })}
          id="startTime"
          name="startTime"
          label={intl.formatMessage({ id: 'BookingDateRangeFilter.startTimeLabel' })}
          onChange={handleStartTimeChange}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'BookingDateRangeFilter.timePlaceholder' })}
          </option>
          {startOptions.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FieldSelect>

        <FieldSelect
          className={classNames(css.timeField, { [css.timeFieldActive]: !!endTime })}
          id="endTime"
          name="endTime"
          label={intl.formatMessage({ id: 'BookingDateRangeFilter.endTimeLabel' })}
          // An end time is meaningless before a start time has been chosen.
          disabled={startMinutes == null}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'BookingDateRangeFilter.timePlaceholder' })}
          </option>
          {endOptions.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FieldSelect>
      </div>
    </div>
  );
};

export default DateTimeFields;
